import { Grid } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { Box } from "@mui/system";
import { OLButton } from "OLButton";
import { useAchievements } from "api/achievements";
import { useAppSelector } from "app/hooks";
import OverflowChipTooltip from "components/Tooltips/OverflowChipTooltip";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { isUndefined } from "lodash";
import { useEffect, useRef, useState } from "react";
import { selectDefaultLanguageData } from "reduxState/translations/translationsSlice";
import {
  AudienceType,
  CampaignBEData,
  CampaignTriggers,
  TransactionCampaigns,
  TriggerStrategyType,
  VisibilityTarget,
} from "types/campaigns";
import { OLDate } from "utilities/dateFormat";
import { KeyValuePair } from "utilities/objects";
import { InternalEvent } from "../../../Common/CampaignTriggers/InternalEvent/helpers";
import { parseMonthlyDays } from "../../../Common/campaignHelpers";
import { SegmentsFetcher } from "../../CampaignForm/Info/Sections/SegmentsFetcher";
import { TiersFetcher } from "../../CampaignForm/Summary/Sections/TiersFetcher";
import { RulesSection } from "./RulesSection";
import { SegmentDisplay } from "./SegmentDisplay";
import { TierDisplay } from "./TierDisplay";
import { TransactionFiltersSection } from "./TransactionFiltersSection";

export const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    header: {
      background: theme.gray50,
      borderRadius: "8px 8px 0 0",
      boxShadow: `0px 1px 0px ${theme.gray200}`,
      color: theme.gray800,
      padding: theme.spacing(6, 8),
    },
    ruleHeader: {
      background: theme.gray50,
      display: "flex",
      flexDirection: "column",
      padding: theme.spacing(6, 8),
      marginTop: 1,
      gap: theme.spacing(2),
    },
    ruleTitle: {
      color: theme.gray800,
      fontSize: 14,
      fontWeight: 500,
    },
    ruleDesc: {
      color: theme.primary800,
      fontSize: 12,
      fontWeight: 400,
    },
    ruleContent: {
      boxShadow: `0px -1px 0px ${theme.gray100}`,
      padding: theme.spacing(8, 12),
    },
    conditionsHeader: {
      color: theme.gray800,
      fontWeight: 500,
    },
    rule: {
      display: "flex",
      flexDirection: "column",
    },
    left: {
      color: theme.gray500,
      textWrap: "nowrap",
    },
    right: {
      display: "flex",
      justifyContent: "flex-end",
      flexWrap: "wrap",
      gap: theme.spacing(6),
      color: theme.gray800,
      textAlign: "right",
    },
    gridContainer: {
      alignItems: "start",
      fontWeight: 500,
      flexWrap: "nowrap",
      gap: theme.spacing(12),
    },
    gridChild: {
      background: "#FEFFFF",
      border: `1px solid ${theme.gray200}`,
      borderRadius: 8,
      boxShadow: "0px 1px 1px rgba(154, 147, 147, 0.25)",
      display: "flex",
      flexDirection: "column",
    },
    content: {
      boxShadow: `0px 1px 0px ${theme.gray100}`,
      display: "flex",
      gap: theme.spacing(10),
      justifyContent: "space-between",
      padding: theme.spacing(8, 12),
    },
    attribute: {
      background: theme.gray100,
      borderRadius: 4,
      color: theme.gray800,
      padding: theme.spacing(4, 8),
      textOverflow: "elipsis",
      overflow: "hidden",
    },
    readOnlyContainer: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(4),
    },
    readOnlyWrapper: {
      display: "flex",
      alignItems: "center",
      marginTop: theme.spacing(8),
    },
    showAllBtn: {
      fontSize: "13px",
      textDecoration: "underline",
      alignSelf: "flex-end",
      "&:hover": {
        textDecoration: "underline",
      },
      margin: theme.spacing(0, 12, 8, 0),
    },
    noShadow: {
      boxShadow: "none",
      paddingBottom: 0,
    },
    advancedSettings: {
      margin: 0,
      maxWidth: "846px",
    },
    vectorMargin: {
      margin: theme.spacing(-7, 0, 0, 2),
    },
    customAttributesWrapper: {
      overflow: "hidden",
    },
  }),
);

interface Props {
  campaignData: CampaignBEData;
  openCustomAttributesModal: () => void;
}

export function CampaignDetails({
  campaignData,
  openCustomAttributesModal,
}: Props) {
  const classes = useStyles();
  const { message } = useMessage();
  const defaultLang = useAppSelector(selectDefaultLanguageData);
  const refElement = useRef<HTMLDivElement>(null);
  const [listOfFetchedElements, setListOfFetchedElements] = useState<any[]>([]);
  const triggerStrategy =
    "triggerStrategy" in campaignData
      ? campaignData.triggerStrategy
      : undefined;

  const chipsContainerMaxHeight = 37;
  const isAchievementCampaign =
    campaignData.trigger === CampaignTriggers.ACHIEVEMENT;

  const { useAchievementList } = useAchievements();
  const { data: achievementList } = useAchievementList();

  const campaignAchievementName =
    isAchievementCampaign &&
    achievementList?.items.find(
      (achievement) =>
        achievement.achievementId === campaignData?.achievementId,
    )?.name;

  const [chipsContainerCurrentHeight, setChipsContainerCurrentHeight] =
    useState(0);
  const isShowAllBtnVisible =
    chipsContainerCurrentHeight > chipsContainerMaxHeight;

  useEffect(() => {
    if (!refElement.current) return;
    const resizeObserver = new ResizeObserver(() => {
      setChipsContainerCurrentHeight(
        refElement.current?.clientHeight as number,
      );
    });
    resizeObserver.observe(refElement.current);
    return () => resizeObserver.disconnect();
  }, []);

  const handleCampaignInternalEventLabel = (event?: InternalEvent) => {
    switch (event) {
      case "MemberAchievementProgressWasChanged":
        return message({ id: "achievementProgressed" });
      case "MemberAddressChanged":
        return message({ id: "memberAddressChanged" });
      case "MemberCompanyDetailsChanged":
        return message({ id: "memberCompanyDetailsChanged" });
      case "MemberDetailsChanged":
        return message({ id: "memberDetailsChanged" });
      case "MemberTierChanged":
        return message({ id: "memberTierChanged" });
      case "MemberWasActivated":
        return message({ id: "memberWasActivated" });
      default:
        return "";
    }
  };

  const handleCampaignTrigger = (data: CampaignBEData) => {
    switch (data.trigger) {
      case CampaignTriggers.INTERNAL_EVENT:
        return `${message({
          id: "internalEvent",
        })}: ${handleCampaignInternalEventLabel(data.event)}`;
      case CampaignTriggers.CUSTOM_EVENT:
        return `${message({
          id: "customEvent",
        })}: ${data.event}`;
      case CampaignTriggers.CUSTOM_EVENT_UNIQUE_CODE:
        return message({
          id: "redemptionCode",
        });
      case CampaignTriggers.TRANSACTION:
        return message({ id: "purchase" });
      case CampaignTriggers.RETURN_TRANSACTION:
        return message({ id: "return" });
      case CampaignTriggers.ACHIEVEMENT:
        return `${message({ id: "achievement" })}: ${campaignAchievementName}`;
      default:
        return message({ id: data.trigger });
    }
  };

  const campaignDataLabels =
    campaignData?.labels && campaignData?.labels?.length > 0 ? (
      <>
        {campaignData?.labels?.map((attribute: KeyValuePair, index: number) => (
          <OverflowChipTooltip
            key={index}
            placement="top"
            label={`${attribute.key}: ${attribute.value}`}
            data-test-id={`single-campaign-custom-attributes-${index}`}
          />
        ))}
      </>
    ) : (
      "-"
    );

  const isCampaignTimeBased = campaignData?.trigger === CampaignTriggers.TIME;

  const triggerStrategyType = isCampaignTimeBased
    ? campaignData?.triggerStrategy?.type
    : "";

  const isTriggerStrategyWeekly =
    triggerStrategyType === TriggerStrategyType.WEEKLY;
  const isTriggerStrategyMonthly =
    triggerStrategyType === TriggerStrategyType.MONTHLY;
  const isTriggerStrategyBirthday =
    triggerStrategyType === TriggerStrategyType.BIRTHDAY;
  const isTriggerStrategyMembershipAnniversary =
    triggerStrategyType === TriggerStrategyType.REGISTRATION_ANNIVERSARY;
  const days =
    isCampaignTimeBased &&
    campaignData.triggerStrategy.executionSchedule?.dayOfWeek;

  const formattedWeekdays =
    days &&
    days
      .map((day) => {
        switch (day) {
          case 0:
            return message({ id: "sunday" });
          case 1:
            return message({ id: "monday" });
          case 2:
            return message({ id: "tuesday" });
          case 3:
            return message({ id: "wednesday" });
          case 4:
            return message({ id: "thursday" });
          case 5:
            return message({ id: "friday" });
          case 6:
            return message({ id: "saturday" });
          default:
            return "";
        }
      })
      .join(", ");

  const campaignTrigger = message({
    id: isTriggerStrategyBirthday
      ? "membersBirthday"
      : isTriggerStrategyMembershipAnniversary
        ? "membershipAnniversary"
        : triggerStrategyType,
  });

  const campaignTypeAndTrigger = [
    {
      left: "campaignType",
      leftDataTestId: "single-campaign-campaign-type-label",
      right: isCampaignTimeBased
        ? message({ id: "timeBased" })
        : message({ id: campaignData?.type }),
      rightDataTestId: "single-campaign-campaign-type-value",
    },
    {
      left: "campaignTrigger",
      leftDataTestId: "single-campaign-campaign-trigger-label",
      right: isCampaignTimeBased
        ? message(
            { id: "triggerStrategyType" },
            {
              type: campaignTrigger,
            },
          )
        : handleCampaignTrigger(campaignData),
      rightDataTestId: "single-campaign-campaign-trigger-value",
    },
    isTriggerStrategyWeekly
      ? {
          left: "repeatOn",
          leftDataTestId: "single-campaign-repeat-on-label",
          right: formattedWeekdays,
          rightDataTestId: "single-campaign-repeat-on-value",
        }
      : {},
    isTriggerStrategyMonthly
      ? {
          left: "repeatOn",
          leftDataTestId: "single-campaign-repeat-on-label",
          right: parseMonthlyDays({
            days: triggerStrategy?.executionSchedule?.dayOfMonth,
            message,
          }),
          rightDataTestId: "single-campaign-repeat-on-value",
        }
      : {},
    isCampaignTimeBased && !isTriggerStrategyBirthday
      ? {
          left: "target",
          leftDataTestId: "single-campaign-campaign-target-label",
          right: campaignData?.audience ? (
            campaignData?.audience?.target === AudienceType.TIER ? (
              <TierDisplay tierIds={campaignData?.audience?.tiers} />
            ) : (
              <SegmentDisplay segmentIds={campaignData?.audience?.segments} />
            )
          ) : (
            message({ id: "everyone" })
          ),
          rightDataTestId: "single-campaign-campaign-target-value",
        }
      : {},
  ];

  const setCampaignVisibility = () => {
    switch (campaignData?.visibility?.target) {
      case VisibilityTarget.TIER:
        return (
          <>
            {message({ id: "targetedTiers" })}:{" "}
            {campaignData?.visibility.tiers?.map((item: string, index) => (
              <TiersFetcher
                id={item}
                key={index}
                setListOfFetchedTiers={(data) =>
                  setListOfFetchedElements((previousArrayState) => [
                    ...previousArrayState,
                    data,
                  ])
                }
              />
            ))}
            {listOfFetchedElements &&
              listOfFetchedElements.map((tier) => tier.name).join(", ")}
          </>
        );
      case VisibilityTarget.SEGMENT:
        return (
          <>
            {message({
              id: "targetedSegments",
            })}
            :{" "}
            {campaignData?.visibility.segments?.map((item: string, index) => (
              <SegmentsFetcher
                id={item}
                key={index}
                setListOfFetchedSegments={(data) => {
                  setListOfFetchedElements((previousArrayState) => [
                    ...previousArrayState,
                    data,
                  ]);
                }}
              />
            ))}{" "}
            {listOfFetchedElements &&
              listOfFetchedElements.map((segment) => segment.name).join(", ")}
          </>
        );
      case VisibilityTarget.NONE:
        return message({ id: "hidden" });
      default:
        return message({ id: "visibleForEveryone" });
    }
  };

  return (
    <>
      <Grid container className={classes.gridContainer}>
        <Grid className={classes.gridChild} item xs={12}>
          <span
            className={classes.header}
            data-test-id="single-campaign-basic-settings-header"
          >
            {message({ id: "basicSettings" })}
          </span>
          {[
            {
              left: `${message({ id: "campaignName" })} (${defaultLang?.name})`,
              leftDataTestId: "single-campaign-campaign-name-label",
              right: campaignData?.name,
              rightDataTestId: "single-campaign-campaign-name-value",
            },
            {
              left: `${message({
                id: "campaignDescription",
              })} (${defaultLang?.name})`,
              leftDataTestId: "single-campaign-campaign-description-label",
              right: campaignData?.description,
              rightDataTestId: "single-campaign-campaign-description-value",
            },
            {
              left: "startDate",
              leftDataTestId: "single-campaign-start-date-label",
              right: OLDate(campaignData?.activity?.startsAt || undefined, {
                formatString: "dd MMM, yyyy hh:mm aaaaa'm'",
              }),
              rightDataTestId: "single-campaign-start-date-value",
            },
            {
              left: "endDate",
              leftDataTestId: "single-campaign-end-date-label",
              right:
                OLDate(campaignData?.activity?.endsAt || undefined, {
                  formatString: "dd MMM, yyyy hh:mm aaaaa'm'",
                }) || undefined,
              rightDataTestId: "single-campaign-end-date-value",
            },
            {
              left: "displayOrder",
              leftDataTestId: "single-campaign-display-order-label",
              right: campaignData?.displayOrder,
              rightDataTestId: "single-campaign-display-order-value",
            },
            {
              left: "campaignVisibility",
              leftDataTestId: "single-campaign-campaign-visibility-label",
              right: setCampaignVisibility(),
              rightDataTestId: "single-campaign-campaign-visibility-value",
            },
          ].map((row) => (
            <span className={classes.content} key={row.left}>
              <span className={classes.left} data-test-id={row.leftDataTestId}>
                {message({ id: row.left })}
              </span>
              <span
                className={classes.right}
                data-test-id={row.rightDataTestId}
              >
                {isUndefined(row.right) ? "-" : row.right}
              </span>
            </span>
          ))}
          <span
            className={`${classes.content} ${
              isShowAllBtnVisible && classes.noShadow
            }`}
          >
            <span
              className={classes.left}
              data-test-id="single-campaign-custom-attributes-label"
            >
              {message({ id: "customAttributes" })}
            </span>

            <div
              className={`${classes.right} ${classes.customAttributesWrapper}`}
              data-test-id="single-campaign-custom-attributes-value"
              ref={refElement}
            >
              {isUndefined(campaignDataLabels) ? "-" : campaignDataLabels}
            </div>
          </span>
          {isShowAllBtnVisible && (
            <OLButton
              className={classes.showAllBtn}
              onClick={openCustomAttributesModal}
              data-test-id="single-campaign-show-all-button"
            >
              {message(
                { id: "showAllElements" },
                { elements: campaignData?.labels?.length },
              )}
            </OLButton>
          )}
        </Grid>
      </Grid>
      <Box mt={8} />
      <Grid container className={classes.gridContainer}>
        <Grid className={classes.gridChild} item xs={12}>
          <span
            className={classes.header}
            data-test-id="single-campaign-type-and-trigger-header"
          >
            {message({ id: "typeTrigger" })}
          </span>
          {campaignTypeAndTrigger
            .filter((item) => item.left)
            .map((row) => (
              <span className={classes.content} key={row.left}>
                <span
                  className={classes.left}
                  data-test-id={row.leftDataTestId}
                >
                  {message({ id: row.left })}
                </span>
                <span
                  className={classes.right}
                  data-test-id={row.rightDataTestId}
                >
                  {isUndefined(row.right) ? "-" : row.right}
                </span>
              </span>
            ))}
        </Grid>
      </Grid>
      {(campaignData as TransactionCampaigns)?.transactionItemsFilters
        ?.length ? (
        <>
          <Box mt={8} />
          <TransactionFiltersSection
            transactionItemsFilters={
              (campaignData as TransactionCampaigns).transactionItemsFilters
            }
          />
        </>
      ) : null}
      <Box mt={8} />
      <RulesSection campaignData={campaignData} />
    </>
  );
}
